import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";

import prizeIconUrl from "../assets/svg/prize.svg";
import websocketService from "../services/websocket";

import "./ActiveOrderProfit.css";

interface ActiveOrderProfitProps {
  order: {
    id: string;
    symbol: string;
    type: "buy" | "sell";
    amount: number;
    openPrice: number;
    potential_profit_win?: number;
    potential_profit_loss?: number;
  };
  className?: string;
}

const ActiveOrderProfit: React.FC<ActiveOrderProfitProps> = ({
  order,
  className = "",
}) => {
  const { prices, currencyPairs } = useSelector(
    (state: RootState) => state.trading,
  );
  const rateLimitUntil = useSelector(
    (state: RootState) => state.trading.rateLimitUntil ?? null,
  );

  // Получаем текущую цену для символа ордера из Redux (fallback)
  const currentPriceData = prices[order.symbol];
  const reduxPrice = currentPriceData?.price ?? null;

  // Локальное состояние для live цены через websocket subscribe (самый свежий источник)
  const [livePrice, setLivePrice] = React.useState<number | null>(reduxPrice);

  // Глобальное состояние для отображения остатка rate-limit (используется в render)
  const [rateRemaining, setRateRemaining] = React.useState<number>(0);

  React.useEffect(() => {
    let mounted = true;
    const unsubscribe = websocketService.subscribePrice(
      order.symbol,
      ({ price, timestamp }) => {
        if (!mounted) return;

        // Конвертируем пришедший server timestamp в client-time (ms)
        const clientTs = websocketService.serverToClientTs(timestamp);

        // Если нет валидного timestamp, принимаем тик (но логируем)
        if (!clientTs) {
          setLivePrice(price);
          return;
        }

        const ageMs = Math.abs(Date.now() - clientTs);
        // Игнорируем явно старые тики (например, старше 10s) — они могут быть из кеша
        if (ageMs > 10_000) {
          // stale tick, пропускаем
          return;
        }

        setLivePrice(price);
      },
    );

    // Cleanup
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [order.symbol, reduxPrice]);

  React.useEffect(() => {
    if (!rateLimitUntil) {
      setRateRemaining(0);
      return;
    }

    const tick = () =>
      setRateRemaining(Math.max(0, rateLimitUntil - Date.now()));
    tick();
    const timer = setInterval(tick, 250);
    return () => clearInterval(timer);
  }, [rateLimitUntil]);

  // Используем livePrice если есть, иначе Redux fallback; считаем 0 валидной ценой
  const currentPrice = livePrice ?? reduxPrice;

  if (currentPrice == null) {
    // Если есть активный rate-limit — показываем обратный отсчет
    if (rateLimitUntil && rateLimitUntil > Date.now()) {
      const seconds = Math.ceil(Math.max(0, rateRemaining) / 1000);

      return (
        <div className={`active-order-profit ${className}`}>
          <div className="profit-loading">
            <span>⏳ Rate limit — try again in {seconds}s</span>
          </div>
        </div>
      );
    }

    return (
      <div className={`active-order-profit ${className}`}>
        <div className="profit-loading">
          <span>📊 Loading...</span>
        </div>
      </div>
    );
  }

  // Рассчитываем предварительный результат
  let priceDiff: number;
  if (order.type === "buy") {
    // Для BUY: выигрыш если текущая цена больше цены открытия
    priceDiff = currentPrice - order.openPrice;
  } else {
    // Для SELL: выигрыш если текущая цена меньше цены открытия
    priceDiff = order.openPrice - currentPrice;
  }

  // Определяем предварительный результат
  let preliminaryResult: "win" | "loss" | "draw";
  let preliminaryProfit: number;

  if (priceDiff > 0) {
    preliminaryResult = "win";
    // Используем potential_profit_win если есть, иначе рассчитываем как 92% от суммы
    preliminaryProfit =
      order.potential_profit_win ||
      Number(
        (
          (currencyPairs.find((p) => p.symbol === order.symbol)
            ?.winPayoutRatio ?? 0) * order.amount
        ).toFixed(2),
      );
  } else if (priceDiff < 0) {
    preliminaryResult = "loss";
    // Используем potential_profit_loss если есть, иначе рассчитываем как -amount
    preliminaryProfit = order.potential_profit_loss || -order.amount;
  } else {
    preliminaryResult = "draw";
    preliminaryProfit = 0; // Возврат ставки
  }

  // Определяем что показывать пользователю
  let displayResult: "win" | "loss" | "draw";
  let displayProfit: number;

  if (preliminaryResult === "win") {
    // Если реально WIN - показываем как есть
    displayResult = "win";
    displayProfit = preliminaryProfit;
  } else if (preliminaryResult === "loss") {
    // Если LOSS - красный фон, красная иконка, но WIN $0
    displayResult = "loss";
    displayProfit = 0;
  } else {
    // Если DRAW - желтый фон, желтая иконка, но WIN $0
    displayResult = "draw";
    displayProfit = 0;
  }

  // Форматируем валюту
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Классы для стилизации
  const resultClass = `profit--${displayResult}`;

  return (
    <div className={`button profit ${resultClass} ${className}`}>
      <img className="profit__icon" src={prizeIconUrl} alt="Prize logo" />
      <div className="profit__amount">
        <span className="profit__value">{displayProfit}</span>
        <span className="profit__currency">$</span>
      </div>
    </div>
  );
};

export default ActiveOrderProfit;
